<!-- ═══════════════════════════════════════════════════════════════════════════
     NEW DESIGN — Financial Data Extractor API  (useFinancialExtractAPI = true)
     Response: { data: { symbol, earnings, margins, growth_ratios, return_ratios } }
     ═══════════════════════════════════════════════════════════════════════════ -->
<ng-container *ngIf="useNewApi">
<section class="fmp-kr-card">

  <button class="fmp-kr-close-btn" aria-label="Close" data-dismiss="modal" type="button">
    <span aria-hidden="true">×</span>
  </button>

  <!-- Loading -->
  <div *ngIf="_keyRatiosFetchInProgress && !krFmpData" class="fmp-kr-state">
    <i class="fa fa-circle-o-notch fa-spin fmp-kr-state-icon"></i>
    <p>Loading key ratios…</p>
  </div>

  <!-- No data -->
  <div *ngIf="!_keyRatiosFetchInProgress && !krFmpData" class="fmp-kr-state fmp-kr-state-empty">
    <i class="fa fa-inbox fmp-kr-state-icon"></i>
    <p>No key ratios data available. Ensure a stock symbol is configured for this company.</p>
  </div>

  <ng-container *ngIf="krFmpData">

    <!-- ── HEADER ── -->
    <div class="fmp-kr-header">
      <div class="fmp-kr-header-left">
        <h2 class="fmp-kr-company">Key Financial Ratios</h2>
        <div class="fmp-kr-badges">
          <span class="fmp-kr-badge fmp-kr-badge-sym">{{ krFmpData.symbol }}</span>
          <!-- <span class="fmp-kr-badge fmp-kr-badge-src">FMP</span> -->
          <span *ngIf="krFmpData.data_quality" class="fmp-kr-badge fmp-kr-badge-periods">
            <i class="fa fa-calendar"></i>&nbsp;{{ krFmpData.data_quality.total_periods }} Annual Periods
          </span>
        </div>
      </div>
      <div class="fmp-kr-header-right">
        <span *ngIf="krFmpData.data_quality" class="fmp-kr-quality">
          <i class="fa fa-check-circle"></i>&nbsp;Validated Data
        </span>
      </div>
    </div>

    <!-- ── TAB NAV ── -->
    <div class="fmp-kr-tabnav">
      <ul class="nav nav-tabs fmp-kr-tabs" role="tablist">
        <li class="nav-item">
          <a class="nav-link active fmp-kr-tab" data-target="#krEarningsTab" data-toggle="tab" href="javascript: void(0);" role="tab">
            <i class="fa fa-bar-chart fmp-kr-tab-icon fmp-kr-icon-green"></i>
            <span>Earnings</span>
          </a>
        </li>
        <li class="nav-item">
          <a class="nav-link fmp-kr-tab" data-target="#krMarginsTab" data-toggle="tab" href="javascript: void(0);" role="tab">
            <i class="fa fa-percent fmp-kr-tab-icon fmp-kr-icon-blue"></i>
            <span>Margins</span>
          </a>
        </li>
        <li class="nav-item">
          <a class="nav-link fmp-kr-tab" data-target="#krGrowthTab" data-toggle="tab" href="javascript: void(0);" role="tab">
            <i class="fa fa-line-chart fmp-kr-tab-icon fmp-kr-icon-purple"></i>
            <span>Growth</span>
          </a>
        </li>
        <li class="nav-item">
          <a class="nav-link fmp-kr-tab" data-target="#krReturnsTab" data-toggle="tab" href="javascript: void(0);" role="tab">
            <i class="fa fa-refresh fmp-kr-tab-icon fmp-kr-icon-teal"></i>
            <span>Returns</span>
          </a>
        </li>
      </ul>
    </div>

    <!-- ── TAB CONTENT ── -->
    <div class="tab-content fmp-kr-body">

      <!-- ══ EARNINGS ══════════════════════════════════════════════════════ -->
      <div class="tab-pane active" id="krEarningsTab" role="tabpanel">
        <div *ngIf="krEarningsRows.length === 0" class="fmp-kr-nodata">
          <i class="fa fa-inbox"></i>&nbsp;No earnings data available.
        </div>
        <ng-container *ngIf="krEarningsRows.length > 0">
          <p class="fmp-kr-section-label">Annual Earnings Summary</p>
          <div class="table-responsive fmp-kr-tbl-wrap">
            <table class="fmp-kr-tbl">
              <thead>
                <tr>
                  <th class="fmp-kr-th-metric">Metric</th>
                  <th *ngFor="let d of krEarningsDates" class="fmp-kr-th-date">{{ d }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of krEarningsRows"
                    [class.fmp-kr-row-highlight]="isKrHighlightKey(row._key)">
                  <td class="fmp-kr-td-metric">
                    <span class="fmp-kr-metric-dot"
                      [ngClass]="{'fmp-kr-dot-green': row._key === 'revenue',
                                  'fmp-kr-dot-blue': row._key === 'operating_income',
                                  'fmp-kr-dot-teal': row._key === 'net_income'}"></span>
                    {{ getKrLabel(row._key) }}
                  </td>
                  <td *ngFor="let d of krEarningsDates" class="fmp-kr-td-val">
                    {{ formatKrValue(row[d], row._key) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </ng-container>
      </div>

      <!-- ══ MARGINS ══════════════════════════════════════════════════════ -->
      <div class="tab-pane" id="krMarginsTab" role="tabpanel">
        <div *ngIf="krMarginsRows.length === 0" class="fmp-kr-nodata">
          <i class="fa fa-inbox"></i>&nbsp;No margin data available.
        </div>
        <ng-container *ngIf="krMarginsRows.length > 0">
          <p class="fmp-kr-section-label">Annual Margin Ratios</p>
          <div class="table-responsive fmp-kr-tbl-wrap">
            <table class="fmp-kr-tbl">
              <thead>
                <tr>
                  <th class="fmp-kr-th-metric">Metric</th>
                  <th *ngFor="let d of krMarginsDates" class="fmp-kr-th-date">{{ d }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of krMarginsRows"
                    [class.fmp-kr-row-highlight]="isKrHighlightKey(row._key)">
                  <td class="fmp-kr-td-metric">
                    <span class="fmp-kr-metric-dot fmp-kr-dot-blue"></span>
                    {{ getKrLabel(row._key) }}
                  </td>
                  <td *ngFor="let d of krMarginsDates" class="fmp-kr-td-val">
                    {{ formatKrValue(row[d], row._key) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </ng-container>
      </div>

      <!-- ══ GROWTH ═════════════════════════════════════════════════════ -->
      <div class="tab-pane" id="krGrowthTab" role="tabpanel">
        <div *ngIf="krGrowthRows.length === 0" class="fmp-kr-nodata">
          <i class="fa fa-inbox"></i>&nbsp;No growth ratio data available.
        </div>
        <ng-container *ngIf="krGrowthRows.length > 0">
          <p class="fmp-kr-section-label">Annual Growth Ratios</p>
          <div class="table-responsive fmp-kr-tbl-wrap">
            <table class="fmp-kr-tbl">
              <thead>
                <tr>
                  <th class="fmp-kr-th-metric">Metric</th>
                  <th *ngFor="let d of krGrowthDates" class="fmp-kr-th-date">{{ d }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of krGrowthRows"
                    [class.fmp-kr-row-highlight]="isKrHighlightKey(row._key)">
                  <td class="fmp-kr-td-metric">
                    <span class="fmp-kr-metric-dot fmp-kr-dot-purple"></span>
                    {{ getKrLabel(row._key) }}
                  </td>
                  <td *ngFor="let d of krGrowthDates" class="fmp-kr-td-val">
                    {{ formatKrValue(row[d], row._key) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </ng-container>
      </div>

      <!-- ══ RETURNS ═════════════════════════════════════════════════════ -->
      <div class="tab-pane" id="krReturnsTab" role="tabpanel">
        <div *ngIf="krReturnRows.length === 0" class="fmp-kr-nodata">
          <i class="fa fa-inbox"></i>&nbsp;No return ratio data available.
        </div>
        <ng-container *ngIf="krReturnRows.length > 0">
          <p class="fmp-kr-section-label">Annual Return Ratios</p>
          <div class="table-responsive fmp-kr-tbl-wrap">
            <table class="fmp-kr-tbl">
              <thead>
                <tr>
                  <th class="fmp-kr-th-metric">Metric</th>
                  <th *ngFor="let d of krReturnDates" class="fmp-kr-th-date">{{ d }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of krReturnRows"
                    [class.fmp-kr-row-highlight]="isKrHighlightKey(row._key)">
                  <td class="fmp-kr-td-metric">
                    <span class="fmp-kr-metric-dot fmp-kr-dot-teal"></span>
                    {{ getKrLabel(row._key) }}
                  </td>
                  <td *ngFor="let d of krReturnDates" class="fmp-kr-td-val">
                    {{ formatKrValue(row[d], row._key) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </ng-container>
      </div>

    </div><!-- /tab-content -->

    <!-- ── FOOTER ── -->
    <div class="fmp-kr-footer">
      <span class="fmp-kr-footer-ts" *ngIf="krFmpData.timestamp">
        <i class="fa fa-clock-o"></i>&nbsp;
        Updated: {{ krFmpData.timestamp | date:'dd MMM yyyy, h:mm a' }}
      </span>
      <!-- <span class="fmp-kr-footer-src">Source: Financial Market Platform (FMP)</span> -->
    </div>

  </ng-container>
</section>
</ng-container>

<!-- ═══════════════════════════════════════════════════════════════════════════
     LEGACY DESIGN — Robocs API  (useFinancialExtractAPI = false)
     ═══════════════════════════════════════════════════════════════════════════ -->
<ng-container *ngIf="!useNewApi">
<section class="card mt-4 key-ratios">
  <button aria-label="Close" class="close" data-dismiss="modal" type="button">
    <span aria-hidden="true">×</span>
  </button>

  <div *ngIf="_keyRatiosFetchInProgress && !keyRatiosNewData" class="kr-state-box">
    <i class="fa fa-circle-o-notch fa-spin fa-2x kr-state-icon"></i>
    <p class="kr-state-text">Loading key ratios…</p>
  </div>

  <div *ngIf="!_keyRatiosFetchInProgress && !keyRatiosNewData" class="kr-state-box kr-state-empty">
    <i class="fa fa-inbox fa-2x kr-state-icon"></i>
    <p class="kr-state-text">Key ratios data not available for this company.</p>
  </div>

  <div *ngIf="keyRatiosNewData" class="nav-tabs-horizontal">
    <ul class="nav nav-tabs inverse" role="tablist">
      <li class="nav-item">
        <a class="nav-link active" data-target="#earningsRatiosTabLeg" data-toggle="tab" href="javascript: void(0);" role="tab">Earnings Ratios</a>
      </li>
      <li class="nav-item">
        <a class="nav-link" data-target="#marginRatiosTabLeg" data-toggle="tab" href="javascript: void(0);" role="tab">Margin Ratios</a>
      </li>
      <li class="nav-item">
        <a class="nav-link" data-target="#growthRatiosTabLeg" data-toggle="tab" href="javascript: void(0);" role="tab">Growth Ratios</a>
      </li>
      <li class="nav-item">
        <a class="nav-link" data-target="#returnsRatiosTabLeg" data-toggle="tab" href="javascript: void(0);" role="tab">Returns Ratios</a>
      </li>
    </ul>

    <div class="tab-content">

      <div class="tab-pane active" id="earningsRatiosTabLeg" role="tabpanel">
        <div class="kr-metrics-row">
          <div class="kr-period-block" *ngIf="keyRatiosNewData?.earnings_ratios?.quarter">
            <div class="kr-period-label">Quarterly</div>
            <table class="table table-bordered kr-table">
              <tr><th>Price / Earnings</th><td>{{ keyRatiosNewData.earnings_ratios.quarter.price_to_earnings_ratio | number:'1.2-2' }}</td></tr>
              <tr><th>Earnings per Share</th><td>{{ keyRatiosNewData.earnings_ratios.quarter.earnings_per_share | number:'1.2-2' }}</td></tr>
              <tr><th>Dividend per Share</th><td>{{ keyRatiosNewData.earnings_ratios.quarter.dividend_per_share | number:'1.2-2' }}</td></tr>
              <tr><th>Dividend Yield (%)</th><td>{{ keyRatiosNewData.earnings_ratios.quarter.dividend_yield_percentage | number:'1.2-2' }}</td></tr>
            </table>
          </div>
          <div class="kr-period-block" *ngIf="keyRatiosNewData?.earnings_ratios?.annual">
            <div class="kr-period-label">Annual</div>
            <table class="table table-bordered kr-table">
              <tr><th>Price / Earnings</th><td>{{ keyRatiosNewData.earnings_ratios.annual.price_to_earnings_ratio | number:'1.2-2' }}</td></tr>
              <tr><th>Earnings per Share</th><td>{{ keyRatiosNewData.earnings_ratios.annual.earnings_per_share | number:'1.2-2' }}</td></tr>
              <tr><th>Dividend per Share</th><td>{{ keyRatiosNewData.earnings_ratios.annual.dividend_per_share | number:'1.2-2' }}</td></tr>
              <tr><th>Dividend Yield (%)</th><td>{{ keyRatiosNewData.earnings_ratios.annual.dividend_yield_percentage | number:'1.2-2' }}</td></tr>
            </table>
          </div>
        </div>
      </div>

      <div class="tab-pane" id="marginRatiosTabLeg" role="tabpanel">
        <div class="kr-metrics-row">
          <div class="kr-period-block" *ngIf="keyRatiosNewData?.margin_ratios?.quarter">
            <div class="kr-period-label">Quarterly</div>
            <table class="table table-striped kr-table">
              <tr><th>Gross Profit Margin</th><td>{{ keyRatiosNewData.margin_ratios.quarter.gross_profit_margin | number:'1.2-2' }}%</td></tr>
              <tr><th>Operating Profit Margin</th><td>{{ keyRatiosNewData.margin_ratios.quarter.operating_profit_margin | number:'1.2-2' }}%</td></tr>
              <tr><th>Net Profit Margin</th><td>{{ keyRatiosNewData.margin_ratios.quarter.net_profit_margin | number:'1.2-2' }}%</td></tr>
              <tr><th>EBITDA Margin</th><td>{{ keyRatiosNewData.margin_ratios.quarter.ebitda_margin | number:'1.2-2' }}%</td></tr>
            </table>
          </div>
          <div class="kr-period-block" *ngIf="keyRatiosNewData?.margin_ratios?.annual">
            <div class="kr-period-label">Annual</div>
            <table class="table table-striped kr-table">
              <tr><th>Gross Profit Margin</th><td>{{ keyRatiosNewData.margin_ratios.annual.gross_profit_margin | number:'1.2-2' }}%</td></tr>
              <tr><th>Operating Profit Margin</th><td>{{ keyRatiosNewData.margin_ratios.annual.operating_profit_margin | number:'1.2-2' }}%</td></tr>
              <tr><th>Net Profit Margin</th><td>{{ keyRatiosNewData.margin_ratios.annual.net_profit_margin | number:'1.2-2' }}%</td></tr>
              <tr><th>EBITDA Margin</th><td>{{ keyRatiosNewData.margin_ratios.annual.ebitda_margin | number:'1.2-2' }}%</td></tr>
            </table>
          </div>
        </div>
      </div>

      <div class="tab-pane" id="growthRatiosTabLeg" role="tabpanel">
        <div class="kr-metrics-row" *ngIf="keyRatiosNewData?.growth_ratios?.annual">
          <div class="kr-period-block">
            <div class="kr-period-label">Annual</div>
            <table class="table table-hover kr-table">
              <tr><th>P/E Growth Ratio</th><td>{{ keyRatiosNewData.growth_ratios.annual.price_to_earnings_growth_ratio | number:'1.2-2' }}</td></tr>
              <tr><th>Revenue per Share</th><td>{{ keyRatiosNewData.growth_ratios.annual.revenue_per_share | number:'1.2-2' }}</td></tr>
              <tr><th>Book Value per Share</th><td>{{ keyRatiosNewData.growth_ratios.annual.book_value_per_share | number:'1.2-2' }}</td></tr>
              <tr><th>Free Cash Flow / Share</th><td>{{ keyRatiosNewData.growth_ratios.annual.free_cash_flow_per_share | number:'1.2-2' }}</td></tr>
            </table>
          </div>
        </div>
      </div>

      <div class="tab-pane" id="returnsRatiosTabLeg" role="tabpanel">
        <div class="kr-metrics-row" *ngIf="keyRatiosNewData?.returns_ratios?.annual">
          <div class="kr-period-block">
            <div class="kr-period-label">Annual</div>
            <table class="table table-hover kr-table">
              <tr><th>Return on Assets</th><td>{{ keyRatiosNewData.returns_ratios.annual.return_on_assets | number:'1.2-2' }}%</td></tr>
              <tr><th>Current Ratio</th><td>{{ keyRatiosNewData.returns_ratios.annual.current_ratio | number:'1.2-2' }}</td></tr>
              <tr><th>Quick Ratio</th><td>{{ keyRatiosNewData.returns_ratios.annual.quick_ratio | number:'1.2-2' }}</td></tr>
              <tr><th>Debt to Equity</th><td>{{ keyRatiosNewData.returns_ratios.annual.debt_to_equity_ratio | number:'1.2-2' }}</td></tr>
            </table>
          </div>
        </div>
      </div>

    </div>
  </div>
</section>
</ng-container>
